'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/browser/safe-local-storage';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

// Spec 077-launchpad-vote-buttons. Device-scoped vote state for the launchpad
// feed. No backend write; persistence is purely client-side via localStorage.
//
// State shape: sparse `Record<pubkey, 'up' | 'down'>` — `null` votes are
// removed rather than stored, so the persisted payload stays compact as
// the user explores more proposals over time.

type Vote = 'up' | 'down' | null;

interface LaunchpadVotesState {
  votes: Record<string, 'up' | 'down'>;
  /** Read the current vote for a proposal. Returns `null` when absent. */
  getVote: (pubkey: string) => Vote;
  /** Write the current vote. `null` deletes the entry; non-null overwrites. */
  setVote: (pubkey: string, vote: Vote) => void;
}

export const useLaunchpadVotesStore: UseBoundStore<StoreApi<LaunchpadVotesState>> =
  createTrackedStore<LaunchpadVotesState>('useLaunchpadVotesStore')(
    persist(
      (set, get) => ({
        votes: {},
        getVote: (pubkey: string): Vote => {
          return get().votes[pubkey] ?? null;
        },
        setVote: (pubkey: string, vote: Vote): void => {
          if (vote === null) {
            const current = get().votes;
            if (!(pubkey in current)) return;
            const next = { ...current };
            delete next[pubkey];
            set({ votes: next });
            return;
          }
          set(
            (state: LaunchpadVotesState): Pick<LaunchpadVotesState, 'votes'> => ({
              votes: { ...state.votes, [pubkey]: vote },
            }),
          );
        },
      }),
      {
        name: 'morfi-launchpad-votes',
        partialize: (state: LaunchpadVotesState): Pick<LaunchpadVotesState, 'votes'> => ({
          votes: state.votes,
        }),
        storage: createJSONStorage(() => safeLocalStorage),
      },
    ),
  );
